import React, { useEffect, useRef, useState } from "react";
import kakaoIcon from "../../assets/img/kakao.png";
import { ReactComponent as ScrollTopIcon } from "../../assets/icons/svg/scrollTop.svg";
import { openKakaoChat } from "../../utils/kakaoChat";

const BUTTON_SIZE = 56;
const EDGE_GAP = 24;
const STACK_GAP = 12;
const SCROLL_TOP_THRESHOLD = 80;

const getVerticalOffset = (showScrollTop) =>
  showScrollTop ? BUTTON_SIZE + STACK_GAP : 0;

const clampAnchorPosition = (x, y, showScrollTop) => {
  const minY = EDGE_GAP + getVerticalOffset(showScrollTop);
  const maxX = Math.max(EDGE_GAP, window.innerWidth - BUTTON_SIZE - EDGE_GAP);
  const maxY = Math.max(minY, window.innerHeight - BUTTON_SIZE - EDGE_GAP);

  return {
    x: Math.min(Math.max(EDGE_GAP, x), maxX),
    y: Math.min(Math.max(minY, y), maxY),
  };
};

const getInitialAnchorPosition = () => {
  if (typeof window === "undefined") {
    return { x: 0, y: 0 };
  }

  return clampAnchorPosition(
    window.innerWidth - BUTTON_SIZE - EDGE_GAP,
    window.innerHeight - BUTTON_SIZE - EDGE_GAP,
    false
  );
};

export default function FloatingChannelButton() {
  const [anchorPosition, setAnchorPosition] = useState(getInitialAnchorPosition);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const showScrollTopRef = useRef(false);
  const dragRef = useRef({
    active: false,
    moved: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  useEffect(() => {
    showScrollTopRef.current = showScrollTop;
  }, [showScrollTop]);

  useEffect(() => {
    const syncScrollState = () => {
      const nextVisible = window.scrollY > SCROLL_TOP_THRESHOLD;

      setShowScrollTop((prevVisible) => {
        if (prevVisible === nextVisible) {
          return prevVisible;
        }

        setAnchorPosition((currentPosition) =>
          clampAnchorPosition(currentPosition.x, currentPosition.y, nextVisible)
        );

        return nextVisible;
      });
    };

    const handleResize = () => {
      setAnchorPosition((currentPosition) =>
        clampAnchorPosition(currentPosition.x, currentPosition.y, showScrollTopRef.current)
      );
    };

    syncScrollState();

    window.addEventListener("scroll", syncScrollState, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", syncScrollState);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    event.preventDefault();

    dragRef.current.active = true;
    dragRef.current.moved = false;
    dragRef.current.pointerId = event.pointerId;
    dragRef.current.startX = event.clientX;
    dragRef.current.startY = event.clientY;
    dragRef.current.originX = anchorPosition.x;
    dragRef.current.originY = anchorPosition.y;

    event.currentTarget.style.cursor = "grabbing";
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();

    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.moved = true;
    }

    setAnchorPosition(
      clampAnchorPosition(
        dragRef.current.originX + dx,
        dragRef.current.originY + dy,
        showScrollTopRef.current
      )
    );
  };

  const finishDrag = (event) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    event.currentTarget.style.cursor = "grab";
    dragRef.current.active = false;
    dragRef.current.pointerId = null;
  };

  const handlePointerUp = (event) => {
    finishDrag(event);
  };

  const handlePointerCancel = (event) => {
    finishDrag(event);
  };

  const handlePrimaryClick = (event) => {
    if (dragRef.current.moved) {
      event.preventDefault();
      return;
    }

    openKakaoChat();
  };

  const handleScrollTopClick = (event) => {
    if (dragRef.current.moved) {
      event.preventDefault();
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const primaryTransform = `translate3d(0, -${getVerticalOffset(showScrollTop)}px, 0)`;
  const scrollTopTransform = showScrollTop
    ? "translate3d(0, 0, 0) scale(1)"
    : "translate3d(0, 14px, 0) scale(0.78)";

  const primaryStyle = {
    left: `${anchorPosition.x}px`,
    top: `${anchorPosition.y}px`,
    transform: primaryTransform,
  };

  const scrollTopStyle = {
    left: `${anchorPosition.x}px`,
    top: `${anchorPosition.y}px`,
    opacity: showScrollTop ? 1 : 0,
    pointerEvents: showScrollTop ? "auto" : "none",
    transform: scrollTopTransform,
  };

  return (
    <>
      <button
        type="button"
        className="floating-channel-button floating-channel-button--primary"
        aria-label="상담 버튼"
        title="상담 버튼"
        style={primaryStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={handlePrimaryClick}
      >
        <img src={kakaoIcon} alt="" aria-hidden="true" />
      </button>

      <button
        type="button"
        className="floating-channel-button floating-channel-button--scroll-top"
        aria-label="맨 위로 이동"
        title="맨 위로 이동"
        style={scrollTopStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={handleScrollTopClick}
      >
        <ScrollTopIcon aria-hidden="true" />
      </button>
    </>
  );
}
